import { useParams } from "react-router-dom";
import GameTable from "@/components/GameTable";
import { FixtureInfo, RowValues, Season, RefereeInfo } from "@/types";
import { useEffect, useState } from "react";
import { AverageCalculatedColumn, AverageCalculatedColumnNOTP90, getRowValuesForFixtureStats, fillNullDataForFixtureStats, FINISHED_STATES_STRING, STAT_KEYS_REFEREES } from "@/utils/stats.tsx";
import PremiumGuard from "@/components/premium-guard";
import { fetchDataAfterRefresh, useUserStore, fetchDataWithArgsAfterRefresh } from "@/components/login/loginStore";
import SEO from '../components/SEO';
import { ExtendedSeason } from "@/components/RankingTables/ranking-table";
import { useTranslation } from 'react-i18next';


export default function Referee() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const user = useUserStore((state) => state);

    const [games, setGames] = useState<FixtureInfo[]>([]);
    const [rowValues, setRowValues] = useState<RowValues[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refereeInfo, setRefereeInfo] = useState<RefereeInfo | null>(null);
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [leaguesNames, setLeaguesNames] = useState<string[]>([]);
    const [currentHalf, setCurrentHalf] = useState<string>('all');

    const fetchStatsData = (windowSize: string, season: string | null, leagues?: string[], homeOrAway?: string | null) => {
        setLoading(true);
        if (windowSize && windowSize != '') {
            fetchDataWithArgsAfterRefresh(['/api/fixture_info_by_referee', '/not_premium_endpoints/player_info.json'],
                {
                    referee_id: id, window: windowSize, state: FINISHED_STATES_STRING,
                    ...season && { seasons_ids: season },
                    ...leagues && { leagues: leagues },
                    ...homeOrAway && { home_or_away: homeOrAway }
                },
                (data) => { setGames(data) }
            );
        }
    };

    const fetchRefereeInfo = () => {
        fetchDataWithArgsAfterRefresh(['/api/referee_by_id', '/not_premium_endpoints/player_info.json'],
            { referee_id: id },
            (data) => { setRefereeInfo(data) }
        );
    };

    useEffect(() => {
        fetchRefereeInfo();
        fetchStatsData('all', null);

        fetchDataAfterRefresh(
            [
                `/api/season_by_referee_id?referee_id=${id}&state=${FINISHED_STATES_STRING}&has_stats=true`,
                "/not_premium_endpoints/season_by_team_id.json"
            ],
            (data: ExtendedSeason[]) => {
                const seasons = data;
                const sortedSeasons: ExtendedSeason[] = seasons.sort((a, b) => {
                    const [startYearA] = a.name.split("/").map(Number);
                    const [startYearB] = b.name.split("/").map(Number);
                    return startYearB - startYearA;
                });
                setSeasons(sortedSeasons);
                const seasonsIds = [...new Set(sortedSeasons.map((season) => season.id))];
                fetchDataWithArgsAfterRefresh(
                    `/api/league_by_seasons_ids`,
                    { seasons_ids: seasonsIds, team_id: id },
                    (data) => {
                        setLeaguesNames(
                            data.map((l: { id: number; name: string }) => ({
                                value: l.id,
                                label: l.name,
                            }))
                        );
                    },
                    false
                );
            }, user.premium
        );
    }
        , [id]);

    const playedAtHome: { [key: number]: number } = {};
    games.forEach((game) => {
        playedAtHome[game.id] = game.home_id;
    });

    useEffect(() => {
        if (games.length) {
            setLoading(true);
            fetchDataWithArgsAfterRefresh(
                ["/api/fixture_statistics", "/not_premium_endpoints/league_stats.json"],
                {
                    fixture_ids: games.map((game: FixtureInfo) => game.id).join(","),
                    current_half: currentHalf
                },
                (data) => {
                    const rv = getRowValuesForFixtureStats(
                        fillNullDataForFixtureStats(data),
                        games,
                        "sum", STAT_KEYS_REFEREES
                    );

                    setRowValues(rv);
                    setLoading(false);
                }
            );


        } else setLoading(false);
    }, [games, currentHalf]);


    return (
        <PremiumGuard>
            <div className="fadeIn">
                <SEO
                    title={refereeInfo?.display_name ? `${refereeInfo.display_name} | Craque Stats` : t('referee.referee_1')}
                    description={refereeInfo?.display_name ? t('referee.referee_2', { name: refereeInfo.display_name }) : t('referee.referee_3')}
                />
                <h1 className="text-3xl">
                    {refereeInfo && refereeInfo.display_name}
                </h1>
                <br />
                {
                    (
                        <GameTable
                            onChange={(windowSize: string, season: string | null, league?: string[], homeOrAway?: string | null) => fetchStatsData(windowSize, season, league, homeOrAway)}
                            loading={loading}
                            seasons={seasons}
                            leaguesNames={leaguesNames}
                            label={t('referee.referee_4')}
                            calculatedColumns={[
                                AverageCalculatedColumnNOTP90,
                                AverageCalculatedColumn,
                            ]}
                            games={games}
                            rowValues={rowValues}
                            typeTable="league"
                            currentHalf={currentHalf} setCurrentHalf={setCurrentHalf}
                        />
                    )
                }
            </div>
        </PremiumGuard>
    );
}